import { useState, useEffect, useRef } from "react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import LoadingButton from "../BaseComponents/LoadingButton/LoadingButton";
import { useCheckEmailExists } from "../../api/people/usePeopleApi";
import styles from "./AddProfessionalCustomerModal.module.css";

const INDUSTRIES = [
  "Airline", "Corporate Travel", "Event Management", "Government",
  "Hospitality", "Incentive Travel", "Logistics", "Management Consulting",
  "Media & Entertainment", "NGO / Non-Profit", "Sports & Recreation",
  "Technology", "Tour Operator", "Travel Agency", "Other",
].sort();

const COUNTRIES = [
  "Austria", "Belgium", "Brazil", "Canada", "China", "Czech Republic",
  "Denmark", "Finland", "France", "Germany", "Greece", "Hungary",
  "India", "Ireland", "Italy", "Japan", "Mexico", "Netherlands",
  "Norway", "Poland", "Portugal", "Romania", "Russia", "South Korea",
  "Spain", "Sweden", "Switzerland", "Turkey", "Ukraine",
  "United Kingdom", "United States",
].sort();

const LANGUAGES = [
  "Arabic", "Chinese", "Danish", "Dutch", "English", "Finnish",
  "French", "German", "Greek", "Hungarian", "Italian", "Japanese",
  "Korean", "Norwegian", "Polish", "Portuguese", "Romanian",
  "Russian", "Spanish", "Swedish", "Turkish", "Ukrainian",
].sort();

const CONTACT_ROLES = [
  "Accounts Manager", "CEO / Director", "Finance Manager",
  "HR Manager", "Operations Manager", "PA / Assistant",
  "Procurement Manager", "Travel Coordinator", "Other",
];

const emptyForm = {
  companyName:  "",
  legalName:    "",
  vatNumber:    "",
  industry:     "",
  website:      "",
  street: "", houseNumber: "", boxNumber: "", city: "", zip: "", country: "",
  hasInvoiceEmail: true,
  invoiceEmail:   "",
  invoiceAddress: "",
  paymentTerms:   "30",
  contactFirstName: "",
  contactLastName:  "",
  hasContactEmail: true,
  contactEmail:     "",
  contactPhone:     "",
  contactRole:      "",
  contactLanguage:  "",
  status: "Active",
};

const REQUIRED_FIELDS = [
  "companyName", "legalName", "vatNumber", "industry", "country",
  "contactFirstName", "contactLastName", "contactPhone",
];

// Phone formatting helpers
const normalizePhone = (val) => val.replace(/[^\d+]/g, '');
const formatPhoneDisplay = (val) => {
  if (!val) return '';
  const n = normalizePhone(val);
  if (n.length > 3) {
    let formatted = n.slice(0, 3);
    for (let i = 3; i < n.length; i += 3) {
      formatted += ' ' + n.slice(i, i + 3);
    }
    return formatted;
  }
  return n;
};

const validateField = (key, value, form) => {
  switch (key) {
    case "companyName":
      if (!value?.trim())        return "Company name is required";
      if (value.trim().length < 2) return "Company name must be at least 2 characters";
      break;
    case "legalName":
      if (!value?.trim())        return "Legal name is required";
      break;
    case "vatNumber":
      if (!value?.trim())        return "VAT number is required";
      break;
    case "industry":
      if (!value)                return "Industry is required";
      break;
    case "country":
      if (!value)                return "Country is required";
      break;
    case "invoiceEmail":
      if (form && !form.hasInvoiceEmail) return "";
      if (!value?.trim())         return "Invoice email is required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Invalid email format";
      break;
    case "contactFirstName":
      if (!value?.trim())        return "Contact first name is required";
      break;
    case "contactLastName":
      if (!value?.trim())        return "Contact last name is required";
      break;
    case "contactEmail":
      if (form && !form.hasContactEmail) return "";
      if (!value?.trim())         return "Contact email is required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Invalid email format";
      break;
    case "contactPhone":
      if (!value?.trim())        return "Contact phone is required";
      { const n = normalizePhone(value);
        if (!n.startsWith('+'))  return "Phone must start with country code (e.g. +32)";
        if (!/^\+[1-9]\d{6,14}$/.test(n)) return "Invalid phone format. Use E.164 (e.g. +32475123456)";
      }
      break;
    default: break;
  }
  return "";
};

// NOTE: must live OUTSIDE the modal component. Defining it inside creates a new
// component type on every render, which makes React unmount/remount the input and
// drops focus after a single keystroke.
const Field = ({
  label, name, type = "text", placeholder, required, children, wide,
  value, error, onChange, onBlur,
}) => (
  <div className={wide ? styles.fieldWide : styles.field}>
    <label>
      {label}
      {required && <span className={styles.required}> *</span>}
    </label>
    {children ?? (
      <input
        type={type}
        value={value}
        onChange={e => onChange(name, e.target.value)}
        onBlur={() => onBlur(name)}
        placeholder={placeholder}
        className={error ? styles.inputError : ""}
      />
    )}
    {error && <span className={styles.errorText}>{error}</span>}
  </div>
);

const AddProfessionalCustomerModal = ({ open, onClose, onSave, company = null, apiError = null }) => {
  const [form,    setForm]    = useState(emptyForm);
  const [errors,  setErrors]  = useState({});
  const [touched, setTouched] = useState({});
  const [invoiceEmailWarning, setInvoiceEmailWarning] = useState("");
  const [contactEmailWarning, setContactEmailWarning] = useState("");
  const emailCheckTimer = useRef(null);
  const { checkEmail } = useCheckEmailExists();

  const isEdit = !!company;

  // Callers may build `company` as an inline object literal, so its identity changes
  // on every parent render. Keying the reset effect on the object would wipe whatever
  // the user has typed; key it on open + company id and read the object from a ref.
  const companyRef = useRef(company);
  companyRef.current = company;
  const companyKey = company ? (company.id ?? company.ref ?? "edit") : null;

  useEffect(() => {
    if (!open) return;
    const company = companyRef.current;
    if (company) {
      setForm({
        companyName:      company.companyName           ?? "",
        legalName:        company.legalName             ?? "",
        vatNumber:        company.vatNumber             ?? "",
        industry:         company.industry              ?? "",
        website:          company.website               ?? "",
        street:           company.address?.street       ?? "",
        houseNumber:      company.address?.houseNumber  ?? "",
        boxNumber:        company.address?.boxNumber    ?? "",
        city:             company.address?.city         ?? "",
        zip:              company.address?.zip          ?? "",
        country:          company.address?.country ?? company.country ?? "",
        hasInvoiceEmail:  company.hasInvoiceEmail !== false,
        invoiceEmail:     company.invoicing?.email      ?? "",
        invoiceAddress:   company.invoicing?.address    ?? "",
        paymentTerms:     company.invoicing?.paymentTerms ?? "30",
        contactFirstName: company.primaryContact?.firstName ?? company.primaryContact?.name?.split(" ")[0]  ?? "",
        contactLastName:  company.primaryContact?.lastName ?? company.primaryContact?.name?.split(" ").slice(1).join(" ") ?? "",
        hasContactEmail:  company.hasContactEmail !== false,
        contactEmail:     company.primaryContact?.email  ?? "",
        contactPhone:     company.primaryContact?.phone ? formatPhoneDisplay(company.primaryContact.phone) : "",
        contactRole:      company.primaryContact?.role   ?? "",
        contactLanguage:  company.primaryContact?.language ?? "",
        status:           company.status                ?? "Active",
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
    setInvoiceEmailWarning("");
    setContactEmailWarning("");
  }, [open, companyKey]);

  const handleChange = (key, value) => {
    // Force email fields to lowercase and strip stray whitespace (e.g. from pasting)
    if (key === "invoiceEmail" || key === "contactEmail") value = value.trim().toLowerCase();
    // Auto-format phone on input
    if (key === "contactPhone") value = formatPhoneDisplay(value);
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: "" }));
    if (key === "invoiceEmail") setInvoiceEmailWarning("");
    if (key === "contactEmail") setContactEmailWarning("");
  };

  const handleBlur = async (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    const err = validateField(key, form[key], form);
    setErrors(prev => ({ ...prev, [key]: err }));

    // Async email duplicate check
    if (key === "invoiceEmail" && form.hasInvoiceEmail && !err && form.invoiceEmail.trim()) {
      clearTimeout(emailCheckTimer.current);
      emailCheckTimer.current = setTimeout(async () => {
        const result = await checkEmail(form.invoiceEmail);
        if (result.exists) {
          const names = result.matches.map(m => m.name).join(', ');
          setInvoiceEmailWarning(`This email already exists in the system (${names}). Duplicate emails are not allowed.`);
        } else {
          setInvoiceEmailWarning("");
        }
      }, 300);
    }
    if (key === "contactEmail" && form.hasContactEmail && !err && form.contactEmail.trim()) {
      clearTimeout(emailCheckTimer.current);
      emailCheckTimer.current = setTimeout(async () => {
        const result = await checkEmail(form.contactEmail);
        if (result.exists) {
          const names = result.matches.map(m => m.name).join(', ');
          setContactEmailWarning(`This email already exists in the system (${names}). Duplicate emails are not allowed.`);
        } else {
          setContactEmailWarning("");
        }
      }, 300);
    }
  };

  const handleClose = () => {
    setForm(emptyForm); setErrors({}); setTouched({});
    setInvoiceEmailWarning(""); setContactEmailWarning("");
    onClose();
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const newErrors = {};
    let isValid = true;
    REQUIRED_FIELDS.forEach(key => {
      const err = validateField(key, form[key], form);
      if (err) { newErrors[key] = err; isValid = false; }
    });

    // Validate email fields conditionally
    if (form.hasInvoiceEmail) {
      const err = validateField("invoiceEmail", form.invoiceEmail, form);
      if (err) { newErrors.invoiceEmail = err; isValid = false; }
    }
    if (form.hasContactEmail) {
      const err = validateField("contactEmail", form.contactEmail, form);
      if (err) { newErrors.contactEmail = err; isValid = false; }
    }

    setErrors(newErrors);
    setTouched(Object.fromEntries([...REQUIRED_FIELDS, "invoiceEmail", "contactEmail"].map(k => [k, true])));
    if (!isValid) return;

    // Block submit if email duplicate warnings exist
    if (invoiceEmailWarning || contactEmailWarning) return;

    setSubmitting(true);
    try {
      const normalizedPhone = normalizePhone(form.contactPhone);
      await onSave({
        companyName:  form.companyName.trim(),
        legalName:    form.legalName.trim(),
        vatNumber:    form.vatNumber.trim(),
        industry:     form.industry,
        website:      form.website.trim() || undefined,
        address: {
          street:      form.street.trim()      || undefined,
          houseNumber: form.houseNumber.trim() || undefined,
          boxNumber:   form.boxNumber.trim()   || undefined,
          city:        form.city.trim()        || undefined,
          zip:         form.zip.trim()         || undefined,
          country:     form.country            || undefined,
        },
        hasInvoiceEmail: form.hasInvoiceEmail,
        invoicing: {
          email:        form.hasInvoiceEmail ? form.invoiceEmail.trim().toLowerCase() : "",
          address:      form.invoiceAddress.trim() || undefined,
          paymentTerms: form.paymentTerms,
        },
        hasContactEmail: form.hasContactEmail,
        primaryContact: {
          name:     `${form.contactFirstName.trim()} ${form.contactLastName.trim()}`.trim(),
          email:    form.hasContactEmail ? form.contactEmail.trim().toLowerCase() : "",
          phone:    normalizedPhone,
          role:     form.contactRole     || undefined,
          language: form.contactLanguage || undefined,
        },
        status: form.status,
      });
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const hasErrors = Object.values(errors).some(Boolean);

  // Wires a Field to form state; Field itself is module-level so it keeps focus.
  const fieldProps = (name) => ({
    name,
    value: form[name],
    error: errors[name],
    onChange: handleChange,
    onBlur: handleBlur,
  });

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={isEdit ? "Edit Company" : "Add New Company"}
      width="860px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>Close</button>
          <LoadingButton className={styles.saveBtn} loading={submitting} loadingText="Saving…" onClick={handleSubmit}>
            {isEdit ? "Save Changes" : "Save Company"}
          </LoadingButton>
        </>
      }
    >

      <div className={styles.iconHeader}>
        <div className={styles.icon}>🏢</div>
        <div className={styles.headerText}>
          <h3>{isEdit ? "Edit Company Details" : "Add New Professional Customer"}</h3>
          <p>
            {isEdit
              ? "Update company information, invoicing details and primary contact"
              : "Register a company, agency or organisation as the legal and financial counterparty for bookings"}
          </p>
        </div>
      </div>

      {apiError && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Save failed:</strong> {apiError}
          </div>
        </div>
      )}

      {hasErrors && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(([key, err]) =>
                err ? <li key={key}>{err}</li> : null
              )}
            </ul>
          </div>
        </div>
      )}

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🏢 COMPANY IDENTITY</h4>
        <div className={styles.formGrid}>

          <Field {...fieldProps("companyName")} label="TRADING NAME" placeholder="e.g. Horizon Travel Group" required />
          <Field {...fieldProps("legalName")}   label="LEGAL NAME"   placeholder="e.g. Horizon Travel Group NV" required />

          <Field {...fieldProps("vatNumber")} label="VAT NUMBER" placeholder="e.g. BE0477.123.456" required />

          <div className={styles.field}>
            <label>INDUSTRY <span className={styles.required}>*</span></label>
            <select
              value={form.industry}
              onChange={e => handleChange("industry", e.target.value)}
              onBlur={() => handleBlur("industry")}
              className={errors.industry ? styles.inputError : ""}
            >
              <option value="">Select industry</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
            {errors.industry && <span className={styles.errorText}>{errors.industry}</span>}
          </div>

          <Field {...fieldProps("website")} label="WEBSITE" placeholder="e.g. https://www.company.com" wide />

        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>
          🏠 REGISTERED ADDRESS
          <span className={styles.optionalTag}> — Optional</span>
        </h4>
        <div className={styles.formGrid}>
          <Field {...fieldProps("street")}      label="STREET"             placeholder="e.g. Rue de la Loi" wide />
          <Field {...fieldProps("houseNumber")} label="HOUSE NO."           placeholder="e.g. 42" />
          <Field {...fieldProps("boxNumber")}   label="BOX NO."             placeholder="e.g. 3A" />
          <Field {...fieldProps("city")}        label="CITY"                placeholder="e.g. Brussels" />
          <Field {...fieldProps("zip")}         label="ZIP / POSTAL CODE"   placeholder="e.g. 1000" />
          <div className={styles.field}>
            <label>COUNTRY <span className={styles.required}>*</span></label>
            <select
              value={form.country}
              onChange={e => handleChange("country", e.target.value)}
              onBlur={() => handleBlur("country")}
              className={errors.country ? styles.inputError : ""}
            >
              <option value="">Select country</option>
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {errors.country && <span className={styles.errorText}>{errors.country}</span>}
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🧾 INVOICING DETAILS</h4>
        <div className={styles.formGrid}>

          <div className={styles.field}>
            <label>COMPANY HAS INVOICE EMAIL</label>
            <div className={styles.yesNoToggle}>
              <button type="button" className={`${styles.yesNoBtn} ${form.hasInvoiceEmail ? styles.yesNoBtnYes : ""}`}
                onClick={() => handleChange("hasInvoiceEmail", true)}>YES</button>
              <button type="button" className={`${styles.yesNoBtn} ${!form.hasInvoiceEmail ? styles.yesNoBtnNo : ""}`}
                onClick={() => { handleChange("hasInvoiceEmail", false); handleChange("invoiceEmail", ""); setInvoiceEmailWarning(""); setErrors(prev => ({ ...prev, invoiceEmail: "" })); }}>NO</button>
            </div>
          </div>

          {form.hasInvoiceEmail && (
            <div className={styles.field}>
              <label>INVOICE EMAIL <span className={styles.required}>*</span></label>
              <input
                type="email"
                value={form.invoiceEmail}
                onChange={e => handleChange("invoiceEmail", e.target.value)}
                onBlur={() => handleBlur("invoiceEmail")}
                placeholder="e.g. accounts@company.com"
                className={errors.invoiceEmail || invoiceEmailWarning ? styles.inputError : ""}
                style={{ textTransform: "lowercase" }}
              />
              {errors.invoiceEmail && <span className={styles.errorText}>{errors.invoiceEmail}</span>}
              {invoiceEmailWarning && (
                <div className={styles.emailWarning}>
                  <span>⚠️</span> <span>{invoiceEmailWarning}</span>
                </div>
              )}
              <span className={styles.helpText}>All invoices and official financial communication will be sent here. Always stored in lowercase.</span>
            </div>
          )}

          <div className={styles.field}>
            <label>PAYMENT TERMS</label>
            <select value={form.paymentTerms} onChange={e => handleChange("paymentTerms", e.target.value)}>
              <option value="0">Immediate (0 days)</option>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
              <option value="45">45 days</option>
              <option value="60">60 days</option>
            </select>
          </div>

          <Field
            {...fieldProps("invoiceAddress")}
            label="INVOICING ADDRESS"
            placeholder="Full invoicing address (if different from registered address)"
            wide
          />

        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>👤 PRIMARY CONTACT</h4>
        <div className={styles.contactNotice}>
          This is the main point of contact at this company. Additional contacts can be added after saving.
        </div>
        <div className={styles.formGrid}>

          <Field {...fieldProps("contactFirstName")} label="FIRST NAME" placeholder="e.g. Thomas" required />
          <Field {...fieldProps("contactLastName")}  label="LAST NAME"  placeholder="e.g. Leclercq" required />

          <div className={styles.field}>
            <label>CONTACT HAS EMAIL</label>
            <div className={styles.yesNoToggle}>
              <button type="button" className={`${styles.yesNoBtn} ${form.hasContactEmail ? styles.yesNoBtnYes : ""}`}
                onClick={() => handleChange("hasContactEmail", true)}>YES</button>
              <button type="button" className={`${styles.yesNoBtn} ${!form.hasContactEmail ? styles.yesNoBtnNo : ""}`}
                onClick={() => { handleChange("hasContactEmail", false); handleChange("contactEmail", ""); setContactEmailWarning(""); setErrors(prev => ({ ...prev, contactEmail: "" })); }}>NO</button>
            </div>
          </div>

          {form.hasContactEmail && (
            <div className={styles.field}>
              <label>EMAIL <span className={styles.required}>*</span></label>
              <input
                type="email"
                value={form.contactEmail}
                onChange={e => handleChange("contactEmail", e.target.value)}
                onBlur={() => handleBlur("contactEmail")}
                placeholder="e.g. t.leclercq@company.com"
                className={errors.contactEmail || contactEmailWarning ? styles.inputError : ""}
                style={{ textTransform: "lowercase" }}
              />
              {errors.contactEmail && <span className={styles.errorText}>{errors.contactEmail}</span>}
              {contactEmailWarning && (
                <div className={styles.emailWarning}>
                  <span>⚠️</span> <span>{contactEmailWarning}</span>
                </div>
              )}
            </div>
          )}

          <div className={styles.field}>
            <label>PHONE <span className={styles.required}>*</span></label>
            <input
              type="tel"
              value={form.contactPhone}
              onChange={e => handleChange("contactPhone", e.target.value)}
              onBlur={() => handleBlur("contactPhone")}
              placeholder="e.g. +32 2 234 567 8"
              className={errors.contactPhone ? styles.inputError : ""}
            />
            {errors.contactPhone && <span className={styles.errorText}>{errors.contactPhone}</span>}
            <span className={styles.helpText}>International E.164 format with country code (e.g. +32475123456)</span>
            {form.contactPhone && !errors.contactPhone && (
              <span className={styles.phoneDisplay}>Stored as: {normalizePhone(form.contactPhone)}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>ROLE / TITLE</label>
            <select value={form.contactRole} onChange={e => handleChange("contactRole", e.target.value)}>
              <option value="">Select role</option>
              {CONTACT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div className={styles.field}>
            <label>PREFERRED LANGUAGE</label>
            <select value={form.contactLanguage} onChange={e => handleChange("contactLanguage", e.target.value)}>
              <option value="">Select language</option>
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

        </div>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>⚙️ STATUS</h4>
        <div className={styles.statusToggle}>
          <input
            type="checkbox"
            id="companyStatus"
            checked={form.status === "Active"}
            onChange={e => handleChange("status", e.target.checked ? "Active" : "Inactive")}
            className={styles.toggleInput}
          />
          <label htmlFor="companyStatus" className={styles.toggleLabel}>
            <span className={styles.toggleSwitch} />
            <span className={styles.toggleText}>{form.status === "Active" ? "Active" : "Inactive"}</span>
          </label>
        </div>
        <span className={styles.helpText}>Only active companies can be selected in new bookings</span>
      </div>

      <div className={styles.businessNotice}>
        <div className={styles.noticeIcon}>📋</div>
        <div className={styles.noticeContent}>
          <strong>Business Rules</strong>
          <ul>
            <li>A Professional Customer is the legal entity — all invoices and payments are at company level</li>
            <li>The VAT number is required for all official invoicing and tax compliance</li>
            <li>Multiple contacts can be added per company — one must always be designated as primary</li>
            <li>A Customer is never automatically created as a Traveler — these are separate identities</li>
          </ul>
        </div>
      </div>

    </BaseModal>
  );
};

export default AddProfessionalCustomerModal;